import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Zap, Clock, X, Flame } from "lucide-react";
import { toast } from "sonner";

/**
 * AgenticPanel — shows Phase 1 (Agentic Follow-up) + Phase 2 (Live Sales Mode)
 * state for one trial user, with quick ops override buttons.
 */
export default function AgenticPanel({ userId, refreshKey }) {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [confirmExit, setConfirmExit] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.get(`/trial-users/${userId}/agentic-state`);
      setState(data);
    } catch {
      /* silent — endpoint may 404 briefly on hot reload */
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  // Live-refresh every 15s so timers stay honest
  useEffect(() => {
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
  }, [load]);

  const cancelFollowup = async () => {
    try {
      await api.post(`/trial-users/${userId}/cancel-followup`);
      toast.success("Follow-up cancelled");
      setConfirmCancel(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to cancel");
    }
  };

  const toggleLiveMode = async (enabled) => {
    try {
      await api.post(`/trial-users/${userId}/live-sales-mode`, {
        enabled,
        reason: enabled ? "manual admin trigger" : "manual admin exit",
      });
      toast.success(enabled ? "Live Sales Mode ON" : "Live Sales Mode OFF");
      setConfirmExit(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  if (!state) {
    return (
      <div
        className="bg-white border border-slate-200 rounded-lg p-5"
        data-testid="agentic-panel-loading"
      >
        <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">
          Agentic AI
        </div>
        <div className="text-xs text-slate-400 mt-2">Loading…</div>
      </div>
    );
  }

  const {
    next_followup_at,
    next_followup_reason,
    followup_count,
    no_followup,
    live_sales_mode,
    live_mode_reason,
    live_mode_message_count,
    live_mode_started_at,
  } = state;

  const scheduledDate = next_followup_at ? new Date(next_followup_at) : null;
  const now = new Date();
  const secondsUntil = scheduledDate
    ? Math.max(0, Math.floor((scheduledDate.getTime() - now.getTime()) / 1000))
    : null;

  const humanCountdown = (s) => {
    if (s == null) return "";
    if (s < 60) return `in ${s}s`;
    if (s < 3600) return `in ${Math.round(s / 60)} min`;
    if (s < 86400) return `in ${Math.round(s / 3600)} h`;
    return `in ${Math.round(s / 86400)} d`;
  };

  return (
    <div
      className="bg-white border border-slate-200 rounded-lg p-5"
      data-testid="agentic-panel"
    >
      <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500 mb-3">
        Agentic AI
      </div>

      {/* ---- Live Sales Mode ---- */}
      {live_sales_mode ? (
        <div
          className="mb-4 rounded-md border border-orange-300 bg-orange-50 p-3"
          data-testid="live-sales-mode-active"
        >
          <div className="flex items-center gap-2">
            <Flame className="h-4 w-4 text-orange-600" />
            <div className="text-sm font-bold text-orange-700 uppercase tracking-wider">
              Live Sales Mode
            </div>
          </div>
          {live_mode_reason && (
            <p className="text-xs text-slate-600 mt-1.5 leading-relaxed">
              {live_mode_reason}
            </p>
          )}
          <div className="text-[11px] text-slate-500 mt-2">
            {live_mode_message_count} nudge{live_mode_message_count === 1 ? "" : "s"} sent
            {live_mode_started_at && (
              <> · started {new Date(live_mode_started_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</>
            )}
          </div>
          {!confirmExit ? (
            <button
              onClick={() => setConfirmExit(true)}
              className="mt-3 text-xs font-semibold text-orange-700 hover:text-orange-900"
              data-testid="live-mode-exit-btn"
            >
              Exit Live Sales Mode
            </button>
          ) : (
            <div className="mt-3 flex items-center gap-2">
              <span className="text-xs text-slate-600">Confirm?</span>
              <button
                onClick={() => toggleLiveMode(false)}
                className="text-xs font-bold text-red-600 hover:underline"
                data-testid="live-mode-exit-confirm"
              >
                Yes, exit
              </button>
              <button
                onClick={() => setConfirmExit(false)}
                className="text-xs text-slate-500 hover:underline"
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      ) : (
        <button
          onClick={() => toggleLiveMode(true)}
          className="mb-4 w-full inline-flex items-center justify-center gap-1.5 rounded-md border border-slate-200 bg-slate-50 hover:bg-orange-50 hover:border-orange-300 hover:text-orange-700 px-3 py-2 text-xs font-semibold text-slate-600 transition"
          data-testid="live-mode-enter-btn"
        >
          <Flame className="h-3.5 w-3.5" /> Start Live Sales Mode
        </button>
      )}

      {/* ---- Agentic Follow-up ---- */}
      <div className="border-t border-slate-100 pt-3">
        <div className="flex items-center gap-1.5 mb-2">
          <Clock className="h-3.5 w-3.5 text-slate-400" />
          <div className="text-xs font-semibold text-slate-700">Next follow-up</div>
        </div>

        {no_followup ? (
          <div className="text-xs text-slate-500" data-testid="no-followup">
            User asked not to be followed up.
          </div>
        ) : next_followup_at ? (
          <div data-testid="followup-scheduled">
            <div className="text-sm font-semibold text-slate-800">
              {humanCountdown(secondsUntil)}
              <span className="text-xs font-normal text-slate-500 ml-1.5">
                · {scheduledDate.toLocaleString([], {
                  month: "short",
                  day: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </span>
            </div>
            {next_followup_reason && (
              <p className="text-xs text-slate-500 mt-1 leading-relaxed italic">
                {next_followup_reason}
              </p>
            )}
            {!confirmCancel ? (
              <button
                onClick={() => setConfirmCancel(true)}
                className="mt-2 text-xs font-semibold text-slate-500 hover:text-red-600"
                data-testid="followup-cancel-btn"
              >
                Cancel follow-up
              </button>
            ) : (
              <div className="mt-2 flex items-center gap-2">
                <span className="text-xs text-slate-600">Confirm?</span>
                <button
                  onClick={cancelFollowup}
                  className="text-xs font-bold text-red-600 hover:underline"
                  data-testid="followup-cancel-confirm"
                >
                  Yes, cancel
                </button>
                <button
                  onClick={() => setConfirmCancel(false)}
                  className="text-xs text-slate-500 hover:underline"
                >
                  No
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="text-xs text-slate-400" data-testid="no-followup-scheduled">
            None scheduled. AI will schedule automatically based on user replies.
          </div>
        )}
        {followup_count > 0 && (
          <div className="text-[10px] text-slate-400 mt-2">
            {followup_count} of 3 lifecycle follow-ups used
          </div>
        )}
      </div>
    </div>
  );
}
